import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, ExternalLink, Globe, Plus, RefreshCw, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { PortalBranding, TenantDomainDto, TenantSettings } from '@imsenta/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Spinner,
  Textarea,
  useToast,
} from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { fmtRelative } from '@/lib/format';
import { useSession } from '@/lib/session';

interface DomainsResponse {
  items: TenantDomainDto[];
  subdomain: string | null;
  target: string | null;
  customDomainsEnabled: boolean;
}

export function PortalSettings() {
  const { can } = useSession();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [hostname, setHostname] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<TenantDomainDto | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['portal-domains'],
    queryFn: () => api.get<DomainsResponse>('/portal/domains'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['portal-domains'] });

  const add = useMutation({
    mutationFn: () => api.post<TenantDomainDto>('/portal/domains', { hostname: hostname.trim() }),
    onSuccess: () => {
      setHostname('');
      setAddError(null);
      invalidate();
      push('Domain added. Publish the DNS record, then verify it.', 'success');
    },
    onError: (err) => setAddError(err instanceof ApiError ? err.message : 'Could not add that domain'),
  });

  const verify = useMutation({
    mutationFn: (id: string) => api.post<TenantDomainDto>(`/portal/domains/${id}/verify`),
    onSuccess: (domain) => {
      invalidate();
      push(domain.verified ? 'Domain verified' : (domain.lastError ?? 'Not verified yet'), domain.verified ? 'success' : 'danger');
    },
  });

  const makePrimary = useMutation({
    mutationFn: (id: string) => api.post(`/portal/domains/${id}/primary`),
    onSuccess: () => {
      invalidate();
      push('Primary domain updated', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not set the primary domain', 'danger'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/portal/domains/${id}`),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
      push('Domain removed', 'success');
    },
  });

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <Card>
        <ErrorState error={error} onRetry={() => void refetch()} title="Could not load portal settings" />
      </Card>
    );
  }

  const readOnly = !can('tenant:manage');

  return (
    <>
      <div className="space-y-5">
        <BrandingCard readOnly={readOnly} />

        <Card>
          <CardHeader
            title="Portal address"
            description="Where your people go to read published policy. They sign in with your identity provider."
          />
          <div className="space-y-4 px-5 py-4">
            {data?.subdomain ? (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-ink-200 bg-ink-50 px-3.5 py-3">
                <Globe className="size-4 shrink-0 text-ink-400" />
                <code className="min-w-0 flex-1 truncate font-mono text-[13px] text-ink-800">https://{data.subdomain}</code>
                <Badge tone="success">ready</Badge>
                <CopyButton value={`https://${data.subdomain}`} label="Portal address" />
                <a href={`https://${data.subdomain}`} target="_blank" rel="noreferrer">
                  <Button size="sm" icon={<ExternalLink className="size-3.5" />}>
                    Open
                  </Button>
                </a>
              </div>
            ) : (
              <Alert tone="info">
                No portal base domain is configured on this server, so automatic subdomains are unavailable. A branded domain still
                works if custom domains are enabled.
              </Alert>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Your own domain"
            description="Point a hostname you control at the portal, so your people see your address rather than ours."
          />

          {!data?.customDomainsEnabled ? (
            <div className="px-5 py-4">
              <Alert tone="warning">Custom domains are switched off on this server.</Alert>
            </div>
          ) : (
            <>
              {!data.items.length ? (
                <EmptyState
                  icon={<Globe className="size-9" />}
                  title="No branded domain yet"
                  description="Add a hostname such as policies.yourcompany.com."
                />
              ) : (
                <ul className="divide-y divide-ink-100">
                  {data.items.map((domain) => (
                    <li key={domain.id} className="px-5 py-4">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <code className="min-w-0 flex-1 basis-52 truncate font-mono text-[13px] text-ink-800">{domain.hostname}</code>
                        {domain.verified ? <Badge tone="success">verified</Badge> : <Badge tone="warning">awaiting DNS</Badge>}
                        {domain.isPrimary ? <Badge tone="brand">primary</Badge> : null}
                        {!readOnly ? (
                          <>
                            <Button
                              size="sm"
                              icon={<RefreshCw className="size-3.5" />}
                              loading={verify.isPending}
                              onClick={() => verify.mutate(domain.id)}
                            >
                              {domain.verified ? 'Re-check' : 'Verify'}
                            </Button>
                            {domain.verified && !domain.isPrimary ? (
                              <Button size="sm" icon={<Star className="size-3.5" />} onClick={() => makePrimary.mutate(domain.id)}>
                                Make primary
                              </Button>
                            ) : null}
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Trash2 className="size-3.5" />}
                              onClick={() => setDeleting(domain)}
                              aria-label={`Remove ${domain.hostname}`}
                            />
                          </>
                        ) : null}
                      </div>

                      {!domain.verified ? (
                        <div className="mt-3 space-y-2 rounded-lg border border-ink-200 bg-ink-50 p-3.5">
                          <p className="text-[12.5px] text-ink-600">
                            Publish these two records, then choose Verify. DNS can take a few minutes to propagate.
                          </p>
                          <DnsRow label="TXT" name={domain.verification.recordName} value={domain.verification.recordValue} />
                          {domain.target ? <DnsRow label="CNAME" name={domain.hostname} value={domain.target} /> : null}
                          {domain.lastError ? (
                            <p className="text-[12.5px] text-rose-600">
                              {domain.lastError}
                              {domain.lastCheckedAt ? ` · checked ${fmtRelative(domain.lastCheckedAt)}` : ''}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <p className="mt-2 flex items-center gap-1.5 text-[12.5px] text-emerald-700">
                          <CheckCircle2 className="size-3.5" />
                          Serving at <code className="font-mono">https://{domain.hostname}</code>
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {!readOnly ? (
                <div className="border-t border-ink-200 px-5 py-4">
                  {addError ? (
                    <div className="mb-3">
                      <Alert tone="danger">{addError}</Alert>
                    </div>
                  ) : null}
                  <div className="flex flex-wrap items-end gap-2">
                    <Field label="Hostname" className="min-w-56 flex-1">
                      <Input
                        value={hostname}
                        onChange={(e) => setHostname(e.target.value)}
                        placeholder="policies.yourcompany.com"
                        className="font-mono text-[13px]"
                      />
                    </Field>
                    <Button
                      variant="primary"
                      icon={<Plus className="size-4" />}
                      loading={add.isPending}
                      disabled={!hostname.trim()}
                      onClick={() => {
                        setAddError(null);
                        add.mutate();
                      }}
                    >
                      Add domain
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </Card>

        <Alert tone="info" title="Why a verified domain matters">
          Verifying a domain proves your organisation controls it. As well as serving the portal, it is what allows single sign-on to
          create and link accounts for addresses at that domain — without it, an identity provider could claim addresses belonging to
          someone else.
        </Alert>
      </div>

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        title={`Remove ${deleting?.hostname}?`}
        message="The portal will stop answering on that hostname, and single sign-on will no longer treat its email domain as yours."
        confirmLabel="Remove domain"
        loading={remove.isPending}
      />
    </>
  );
}

function DnsRow({ label, name, value }: { label: string; name: string; value: string }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[70px_minmax(0,1fr)_auto] sm:items-center sm:gap-2">
      <Badge tone="neutral">{label}</Badge>
      <div className="min-w-0">
        <code className="block truncate font-mono text-[12px] text-ink-700">{name}</code>
        <code className="block truncate font-mono text-[12px] text-ink-500">{value}</code>
      </div>
      <CopyButton value={value} label={`${label} value`} />
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const { push } = useToast();
  return (
    <Button
      size="sm"
      icon={<Copy className="size-3.5" />}
      aria-label={`Copy ${label}`}
      onClick={() => {
        void navigator.clipboard.writeText(value);
        push(`${label} copied`, 'success');
      }}
    />
  );
}

function BrandingCard({ readOnly }: { readOnly: boolean }) {
  const { push } = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => api.get<{ settings: TenantSettings }>('/tenant'),
  });

  const [branding, setBranding] = useState<PortalBranding>({
    title: null,
    tagline: null,
    logoDataUri: null,
    accentColor: '#2f34c9',
    supportEmail: null,
  });

  useEffect(() => {
    if (data?.settings.branding) setBranding(data.settings.branding);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.patch('/tenant', { settings: { branding } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      push('Branding saved', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not save branding', 'danger'),
  });

  return (
    <Card>
      <CardHeader title="Branding" description="How your portal looks to the people reading your policy." />
      <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
        <Field label="Portal title" hint="Defaults to your organisation name.">
          <Input
            value={branding.title ?? ''}
            disabled={readOnly}
            onChange={(e) => setBranding({ ...branding, title: e.target.value || null })}
            placeholder="Northwind Policy Library"
          />
        </Field>
        <Field label="Support contact" hint="Shown in the portal footer.">
          <Input
            type="email"
            value={branding.supportEmail ?? ''}
            disabled={readOnly}
            onChange={(e) => setBranding({ ...branding, supportEmail: e.target.value || null })}
            placeholder="compliance@yourcompany.com"
          />
        </Field>
        <Field label="Tagline" className="sm:col-span-2">
          <Textarea
            rows={2}
            value={branding.tagline ?? ''}
            disabled={readOnly}
            onChange={(e) => setBranding({ ...branding, tagline: e.target.value || null })}
            placeholder="What applies to you, and when."
          />
        </Field>
        <Field label="Accent colour">
          <div className="flex gap-2">
            <input
              type="color"
              value={branding.accentColor}
              disabled={readOnly}
              onChange={(e) => setBranding({ ...branding, accentColor: e.target.value })}
              className="h-9 w-10 cursor-pointer rounded border border-ink-200 bg-white p-0.5"
            />
            <Input
              value={branding.accentColor}
              disabled={readOnly}
              onChange={(e) => setBranding({ ...branding, accentColor: e.target.value })}
              className="font-mono text-[12px]"
            />
          </div>
        </Field>
        <Field label="Logo" hint="PNG, JPEG or SVG, under about 1 MB.">
          <div className="flex items-center gap-3">
            {branding.logoDataUri ? <img src={branding.logoDataUri} alt="" className="h-9 max-w-28 object-contain" /> : null}
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              disabled={readOnly}
              className="text-[12.5px]"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 1_400_000) {
                  push('Please choose an image under about 1 MB', 'danger');
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => setBranding({ ...branding, logoDataUri: String(reader.result) });
                reader.readAsDataURL(file);
              }}
            />
            {branding.logoDataUri && !readOnly ? (
              <Button size="sm" onClick={() => setBranding({ ...branding, logoDataUri: null })}>
                Remove
              </Button>
            ) : null}
          </div>
        </Field>
      </div>
      {!readOnly ? (
        <div className="flex justify-end border-t border-ink-200 px-5 py-3">
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Save branding
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
